import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button } from "nlib/ui";
import { checkAuditFetching, getAuditDataCount } from "selectors/audit";
import { getCustomReportsData } from "selectors/customReports";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import AmountDeviationFilters from "./lib/AmountDeviationFilters";
import Arrays from "utils/Arrays";
import AuditSections, { AUDIT_TRANSACTIONS_ROUTE } from "nlib/pages/AuditPage/AuditSections";
import AuditSubsectionRoutes from "const/AuditSubsectionRoutes";
import BulkActions from "nlib/common/TransactionsTable/lib/BulkActions";
import Constants from "const/Constants";
import EmptyState from "nlib/pages/AuditPage/lib/EmptyState";
import Filters from "./lib/Filters";
import React, { useEffect, useMemo } from "react";
import SimilarPayeesTable from "./lib/SimilarPayeesTable";
import TransactionsTable from "./lib/TransactionsTable";
import UnusualAmountTable from "./lib/UnusualAmountTable";
import UnusualCategoriesTable from "./lib/UnusualCategoriesTable";
import UnusualTaxesTable from "./lib/UnusualTaxesTable";
import useAuditUtils from "hooks/useAuditUtils";
import useEnvVars from "hooks/useEnvVars";

const TransactionsContent = (props) => {
  const {
    auditData,
    refetchData,
    disabled,
    count,
    simplifiedLayout,
    currentRoute,
    onEditReportClick,
    onDeleteReportClick
  } = props;

  const [{ pageSize = String(Constants.TABLE_PAGE_SIZE), page }, setEnvVars] = useEnvVars();

  const { uiTexts } = useSelector(getTextsData);

  const auditFetching = useSelector(checkAuditFetching);

  const customReportsData = useSelector(getCustomReportsData);

  const auditDataCount = useSelector(getAuditDataCount);

  const currentSectionData = Arrays.findByKey(AuditSections, "route", AUDIT_TRANSACTIONS_ROUTE);

  const currentItemData = Arrays.findByKey(currentSectionData.items, "route", currentRoute)
    || Arrays.findById(customReportsData, currentRoute);

  const dataFetching = auditFetching;

  const {
    transactionsState,
    selectedIds,
    allItemsSelected,
    closedBookDateTransactionIds,
    setSelectedIds,
    onTransactionChange,
    onBulkActionsEdit,
    onBulkActionsCancel,
    onHeaderCheckBoxChange
  } = useAuditUtils({ transactionsData: auditData, currentRoute, refetchData });

  const allSelectedUsesItems = useMemo(() => {
    return selectedIds.every((id) => Arrays.findById(auditData, id)?.usesItems);
  }, [auditData, selectedIds]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => Arrays.findById(auditData, id)));
  }, [auditData, setSelectedIds]);
  useEffect(() => {
    if (dataFetching || auditData.length || (!count && !auditDataCount) || !page) {
      return;
    }
    setEnvVars({ page: Math.ceil((auditDataCount || count) / pageSize) });
  }, [
    page,
    pageSize,
    dataFetching,
    auditData.length,
    count,
    auditDataCount,
    setEnvVars
  ]);

  const title = currentItemData?.id ? currentItemData.name : uiTexts[currentItemData?.nameLangId];

  return (
    <>
      <div className={Css.header}>
        <div className={Css.title} title={title}>
          {`${uiTexts[currentSectionData.nameLangId]}: ${title}`}
        </div>
        {currentRoute === AuditSubsectionRoutes.AMOUNT_DEVIATION && (
          <AmountDeviationFilters disabled={disabled} />
        )}
        {!!currentItemData?.id && (
          <div className={Css.buttons}>
            <Button primary light icon={Icons.PencilSimple} onClick={onEditReportClick}>{uiTexts.edit}</Button>
            <Button danger light icon={Icons.Trash} onClick={onDeleteReportClick}>{uiTexts.delete}</Button>
          </div>
        )}
      </div>
      {!!selectedIds.length && (
        <BulkActions
          audit
          disabled={disabled}
          usesItems={allSelectedUsesItems}
          showMarkAsCorrect={currentRoute !== AuditSubsectionRoutes.ALL_CATEGORIZED}
          selectedTransactions={selectedIds}
          onCancel={onBulkActionsCancel}
          onEdit={onBulkActionsEdit} />
      )}
      <Filters disabled={disabled} />
      <div className={Css.content}>
        {auditData.length
          ? (() => {
            switch (currentRoute) {
              case AuditSubsectionRoutes.UNUSUAL_CATEGORY:
                return (
                  <UnusualCategoriesTable
                    disabled={dataFetching}
                    currentRoute={currentRoute}
                    refetchData={refetchData} />
                );
              case AuditSubsectionRoutes.UNUSUAL_AMOUNT:
                return (
                  <UnusualAmountTable
                    disabled={dataFetching}
                    currentRoute={currentRoute}
                    refetchData={refetchData} />
                );
              case AuditSubsectionRoutes.UNUSUAL_TAX:
                return (
                  <UnusualTaxesTable
                    disabled={dataFetching}
                    currentRoute={currentRoute}
                    refetchData={refetchData} />
                );
              case AuditSubsectionRoutes.SIMILAR_PAYEE:
                return (
                  <SimilarPayeesTable
                    disabled={dataFetching}
                    currentRoute={currentRoute}
                    refetchData={refetchData} />
                );
              default:
                return (
                  <TransactionsTable
                    key={currentRoute}
                    disabled={disabled || dataFetching}
                    simplifiedLayout={simplifiedLayout}
                    data={auditData}
                    count={count}
                    selectedIds={selectedIds}
                    allItemsSelected={allItemsSelected}
                    closedBookDateTransactionIds={closedBookDateTransactionIds}
                    transactionsState={transactionsState}
                    currentRoute={currentRoute}
                    refetchData={refetchData}
                    setSelectedIds={setSelectedIds}
                    onHeaderCheckBoxChange={onHeaderCheckBoxChange}
                    onTransactionChange={onTransactionChange} />
                );
            }
          })()
          : <EmptyState fetching={dataFetching} />}
      </div>
    </>
  );
};

export default TransactionsContent;
